import type { BondOrder } from '../domain/editor.ts';
import type { ChemMoleculeV1Document } from '../domain/moleculeProjection.ts';
import type { ChemIsomerV1Document } from '../domain/isomerDocument.ts';
import { elementSymbol } from './elements.ts';
import { maximumSupportedValence } from './chemicalValidation.ts';

// The classic SMILES "organic subset": these elements may be written unbracketed, with RDKit's
// parser automatically filling implicit hydrogens via standard valence rules -- exactly how every
// user-typed SMILES already behaves elsewhere in this app. Any other element, or any atom carrying
// a nonzero formal charge or a set isotope, must use bracket notation, which suppresses that
// auto-fill; those atoms get their hydrogens written as explicit synthetic [H] neighbors below
// (computed via the existing, already-trusted maximumSupportedValence table) rather than an
// H-count inside the bracket, so this never needs its own separate valence-filling logic.
const ORGANIC_SUBSET = new Set([5, 6, 7, 8, 15, 16, 9, 17, 35, 53]); // B C N O P S F Cl Br I

const IMPLICIT_HYDROGEN_KEY = ' H';

interface AdjacencyEntry {
  readonly neighborId: string;
  readonly edgeId: string;
}

interface WriteNeighbor {
  readonly key: string; // real nodeId, or IMPLICIT_HYDROGEN_KEY for a synthetic H
  readonly bondOrder: BondOrder;
  // The SMILES bond token written before this neighbour: '' | '=' | '#' | '$', or the RDKit dative
  // extension '->' / '<-' (B4 #273). Direction is relative to the write order parent<token>child.
  readonly bondToken: string;
  // True when the atom owning this WriteNeighbor list is the dative bond's donor -- that end's
  // valence / implicit-H is unaffected, so it is excluded from the explicit-H fill count.
  readonly dativeDonor?: boolean;
  readonly ringDigit?: number; // set only for a back-edge (ring closure) reference
  readonly childNodeId?: string; // set only for a tree edge (real recursion target)
}

function bondTokenFor(bond: ChemMoleculeV1Document['bonds'][string], fromNodeId: string): string {
  if (bond.type === 'dative') return bond.donorAtomId === fromNodeId ? '->' : '<-';
  // ADR 0024: '$', SMILES's quadruple bond. SMILES has nothing for quintuple or sextuple; this
  // string only lays out a depiction (generateWedgeAccurateIsomerDepiction), so '$' stands in.
  return bond.order === 1 ? '' : bond.order === 2 ? '=' : bond.order === 3 ? '#' : '$';
}

interface TreeInfo {
  readonly children: ReadonlyMap<string, readonly WriteNeighbor[]>;
  readonly parentKey: ReadonlyMap<string, string | undefined>;
}

function buildAdjacency(molecule: ChemMoleculeV1Document): Map<string, AdjacencyEntry[]> {
  const adjacency = new Map<string, AdjacencyEntry[]>();
  for (const node of molecule.graph.nodes) adjacency.set(node.id, []);
  for (const edge of molecule.graph.edges) {
    adjacency.get(edge.source)!.push({ neighborId: edge.target, edgeId: edge.id });
    adjacency.get(edge.target)!.push({ neighborId: edge.source, edgeId: edge.id });
  }
  return adjacency;
}

// Pass 1: pure graph exploration (no string building). Determines the DFS spanning tree and
// assigns ring-closure digits to back edges. This has to happen before any text is written,
// because a ring-closure digit belongs at BOTH its endpoints, and the earlier-visited endpoint's
// text is already written by the time a later traversal step discovers the closing edge.
function computeTree(
  molecule: ChemMoleculeV1Document,
  adjacency: ReadonlyMap<string, AdjacencyEntry[]>,
  startNodeId: string,
  visited: Set<string>
): TreeInfo {
  const children = new Map<string, WriteNeighbor[]>();
  const parentKey = new Map<string, string | undefined>();
  const ringDigitByEdgeId = new Map<string, number>();
  let nextRingDigit = 1;

  const visit = (nodeId: string, parentEdgeId: string | undefined): void => {
    visited.add(nodeId);
    const nodeChildren: WriteNeighbor[] = [];
    children.set(nodeId, nodeChildren);
    for (const { neighborId, edgeId } of adjacency.get(nodeId) ?? []) {
      if (edgeId === parentEdgeId) continue;
      const bond = molecule.bonds[edgeId]!;
      // Order 0 is only a hydrogen bond, which is never in a molecule document (ADR 0022 D4).
      const order = bond.order === 0 ? 1 : bond.order;
      const bondToken = bondTokenFor(bond, nodeId);
      const dativeDonor = bond.type === 'dative' && bond.donorAtomId === nodeId;
      if (!visited.has(neighborId)) {
        parentKey.set(neighborId, nodeId);
        nodeChildren.push({ key: neighborId, bondOrder: order, bondToken, dativeDonor, childNodeId: neighborId });
        visit(neighborId, edgeId);
      } else {
        const existingDigit = ringDigitByEdgeId.get(edgeId);
        const digit = existingDigit ?? nextRingDigit++;
        if (existingDigit === undefined) ringDigitByEdgeId.set(edgeId, digit);
        nodeChildren.push({ key: neighborId, bondOrder: order, bondToken, dativeDonor, ringDigit: digit });
      }
    }
  };
  parentKey.set(startNodeId, undefined);
  visit(startNodeId, undefined);
  return { children, parentKey };
}

// Permutation parity between two orderings of the same 4 keys, via cycle decomposition: a cycle
// of length L is L-1 transpositions, so the total transposition count's parity is the answer.
// Validated (see docs/model or the plan for this change) against RDKit's own CIP output: a single
// transposition of a declared tetrahedral ligand order inverts the represented configuration.
export function permutationParity(reference: readonly string[], actual: readonly string[]): 'even' | 'odd' {
  const indexInReference = new Map(reference.map((key, index) => [key, index]));
  const permutation = actual.map((key) => {
    const index = indexInReference.get(key);
    if (index === undefined) throw new Error(`Isomer ligand ${key} does not match this atom's actual neighbors.`);
    return index;
  });
  const visited: boolean[] = Array.from({ length: permutation.length }, () => false);
  let transpositions = 0;
  for (let start = 0; start < permutation.length; start++) {
    if (visited[start]) continue;
    let cycleLength = 0;
    let index = start;
    while (!visited[index]) {
      visited[index] = true;
      index = permutation[index]!;
      cycleLength++;
    }
    transpositions += cycleLength - 1;
  }
  return transpositions % 2 === 0 ? 'even' : 'odd';
}

function chargeToken(formalCharge: number): string {
  if (formalCharge === 0) return '';
  const magnitude = Math.abs(formalCharge);
  return (formalCharge > 0 ? '+' : '-') + (magnitude > 1 ? String(magnitude) : '');
}

// Given the isomer's declared ligand order/winding for a stereocenter and the order its neighbors
// are actually about to be written in (DFS parent, if any, first -- unavoidable, since a
// non-root atom's first SMILES neighbor is always whichever atom the traversal arrived from --
// followed by children in traversal order), returns the correctly-adjusted @/@@ token.
function stereoTag(isomer: ChemIsomerV1Document, nodeId: string, writtenOrder: readonly string[]): string {
  const declared = isomer.stereochemistry.tetrahedral[nodeId];
  if (!declared) return '';
  const declaredKeys = declared.ligands.map((ligand) =>
    'implicitHydrogen' in ligand ? IMPLICIT_HYDROGEN_KEY : ligand.atomId
  );
  const parity = permutationParity(declaredKeys, writtenOrder);
  const clockwise = declared.winding === 'clockwise';
  const flip = parity === 'odd';
  return clockwise !== flip ? '@@' : '@';
}

export interface MoleculeSmilesResult {
  readonly smiles: string;
  // The original Molecule node id for each atom position AS WRITTEN in `smiles`, in order --
  // `null` for a synthetic [H]. RDKit reports parsed-atom order matching input text order, so
  // this is how a caller correlates RDKit's per-atom output (coordinates, wedge codes) back to
  // the Molecule's own node ids, after RDKit's own depiction generation drops explicit H atoms
  // back to implicit form (see the plan/issue for this change).
  readonly atomOrder: readonly (string | null)[];
}

// Builds a SMILES string for a Molecule+Isomer pair, with explicit bracket atoms wherever needed
// and @/@@ tetrahedral stereo tags computed to exactly match the Isomer's declared winding --
// see the plan/issue for this change for the RDKit-validated reasoning behind the stereo-tag
// computation. Double-bond cis/trans is deliberately NOT encoded (bondStereoValues renders it
// directly from structure.stereochemistry.doubleBonds, no wedge or SMILES encoding needed).
// Throws (rather than silently producing something wrong) for radicals, which have no reliable
// standard SMILES encoding -- callers should treat this as "wedge-accurate rendering unavailable
// for this Isomer" and fall back accordingly, not as an unexpected condition.
export function moleculeIsomerToSmiles(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document
): MoleculeSmilesResult {
  if (Object.values(molecule.atoms).some((atom) => atom.radicalElectronCount !== undefined)) {
    throw new Error('Radicals are not supported by the SMILES-based depiction path.');
  }
  const adjacency = buildAdjacency(molecule);
  const visited = new Set<string>();
  const components: string[] = [];
  const atomOrder: (string | null)[] = [];

  for (const node of molecule.graph.nodes) {
    if (visited.has(node.id)) continue;
    const tree = computeTree(molecule, adjacency, node.id, visited);
    components.push(writeComponent(molecule, isomer, tree, node.id, atomOrder));
  }
  return { smiles: components.join('.'), atomOrder };
}

function writeComponent(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  tree: TreeInfo,
  rootNodeId: string,
  atomOrder: (string | null)[]
): string {
  const writeAtom = (nodeId: string): string => {
    atomOrder.push(nodeId);
    const atom = molecule.atoms[nodeId]!;
    const charge = molecule.formalCharges[nodeId]!;
    const symbol = elementSymbol(atom.atomicNumber);
    const stereoEntry = isomer.stereochemistry.tetrahedral[nodeId];
    const isStereocenter = stereoEntry !== undefined;
    const hasImplicitHLigand = stereoEntry?.ligands.some((ligand) => 'implicitHydrogen' in ligand) ?? false;
    const parentId = tree.parentKey.get(nodeId);
    const treeChildren = tree.children.get(nodeId) ?? [];

    // Explicit synthetic [H] neighbor for a declared implicit-hydrogen ligand -- written as its
    // own real branch atom, sidestepping SMILES's implicit-H-in-a-bracket ordering rules entirely.
    const syntheticHydrogens: WriteNeighbor[] = hasImplicitHLigand
      ? [{ key: IMPLICIT_HYDROGEN_KEY, bondOrder: 1, bondToken: '' }]
      : [];

    const needsBracket =
      isStereocenter ||
      syntheticHydrogens.length > 0 ||
      !ORGANIC_SUBSET.has(atom.atomicNumber) ||
      charge !== 0 ||
      atom.isotope !== undefined;

    // For a non-stereocenter atom that still needs bracket notation (charge/isotope/non-organic
    // element), fill its hydrogens as explicit [H] neighbors too, computed via the same
    // already-trusted valence table chemicalValidation.ts uses for its own valence checks --
    // brackets suppress SMILES's normal auto-fill, so *something* has to supply this count.
    const explicitFillHydrogens: WriteNeighbor[] =
      !isStereocenter && needsBracket
        ? Array.from(
            {
              length: Math.max(
                0,
                (maximumSupportedValence(atom.atomicNumber, charge) ?? 0) -
                  treeChildren.reduce((sum, child) => sum + (child.dativeDonor ? 0 : child.bondOrder), 0)
              )
            },
            () => ({ key: IMPLICIT_HYDROGEN_KEY, bondOrder: 1 as const, bondToken: '' })
          )
        : [];

    const allChildren = [...syntheticHydrogens, ...explicitFillHydrogens, ...treeChildren];
    const writtenOrder = [...(parentId !== undefined ? [parentId] : []), ...allChildren.map((child) => child.key)];
    const stereo = isStereocenter ? stereoTag(isomer, nodeId, writtenOrder) : '';

    const bracketText = needsBracket ? `[${atom.isotope ?? ''}${symbol}${stereo}${chargeToken(charge)}]` : symbol;

    const ringTokens = allChildren
      .filter((child) => child.ringDigit !== undefined)
      .map((child) => `${child.bondToken}${child.ringDigit! > 9 ? `%${child.ringDigit}` : child.ringDigit}`)
      .join('');

    const writeChild = (child: WriteNeighbor): string => {
      if (child.key === IMPLICIT_HYDROGEN_KEY) {
        atomOrder.push(null);
        return '[H]';
      }
      return writeAtom(child.childNodeId!);
    };
    const branchChildren = allChildren.filter((child) => child.ringDigit === undefined);
    const branchText = branchChildren
      .map((child, index) => {
        const isLast = index === branchChildren.length - 1;
        const withBond = `${child.bondToken}${writeChild(child)}`;
        return isLast ? withBond : `(${withBond})`;
      })
      .join('');

    return `${bracketText}${ringTokens}${branchText}`;
  };

  return writeAtom(rootNodeId);
}
